// Drive a scan (core/devices/rfScan) from what the receiver hears.
//
// Unlike useRfLearn this does NOT collapse a burst: the scan counts the repeats
// itself, because a repeat is exactly what tells a real press from a corrupted
// frame.
//
// ⚠️ LISTENING IS ALL THIS DOES. The receiver also POSTs every press to the
// server, which publishes the mapped actions; acting on `RfV` here would fire
// every RF-driven switch twice.

import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribeTopic, unsubscribeTopic, addMessageListener } from '../mqtt/mqttClient';
import { parseRfV } from '../devices/rfCodes';
import {
  startScan, onFrame, skipStep, redoLast,
} from '../devices/rfScan';

/**
 * @param receiverUno  the receiver to listen to, or '' for every receiver
 * @param known        Map from rfScan.namedCodeIndex — codes already named
 * -> { scan, begin(type), skip, redo, reset }
 *    `scan` is null until begin() is called.
 */
export default function useRfScan(receiverUno, known) {
  const [scan, setScan] = useState(null);
  const knownRef = useRef(known);
  knownRef.current = known;
  const active = !!scan;

  useEffect(() => {
    if (!active) return undefined;
    const topic = receiverUno ? `${receiverUno}/client` : '#';
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (receiverUno && t !== topic) return;
      const hit = parseRfV(payload);
      if (!hit) return;
      const from = String(t || '').split('/')[0];
      setScan((s) => onFrame(s, { ...hit, receiverUno: from }, knownRef.current || new Map()));
    });
    return () => {
      off();
      unsubscribeTopic(topic);
    };
  }, [active, receiverUno]);

  const begin = useCallback((type) => setScan(startScan(type)), []);
  const skip = useCallback(() => setScan((s) => skipStep(s)), []);
  const redo = useCallback(() => setScan((s) => redoLast(s)), []);
  const reset = useCallback(() => setScan(null), []);

  return {
    scan, begin, skip, redo, reset,
  };
}
